import React from 'react';

const SpokenLanguageSummary = ({ spokenLanguageData }) => {
  // Helper function to determine color based on score value
  const getColorForScore = (score) => {
    const numScore = parseInt(score);
    if (numScore <= 3) {
      return 'text-red-600 bg-red-100';
    } else if (numScore <= 6) {
      return 'text-yellow-600 bg-yellow-100';
    } else if (numScore <= 8) {
      return 'text-blue-600 bg-blue-100';
    } else {
      return 'text-green-600 bg-green-100';
    }
  };

  // Helper function to get CEFR level color
  const getColorForCEFR = (level) => {
    if (!level) return 'bg-gray-200 text-gray-700';
    
    switch(level.charAt(0)) {
      case 'A':
        return 'bg-red-100 text-red-700';
      case 'B':
        return 'bg-yellow-100 text-yellow-700';
      case 'C':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  };
  
  // Helper function to get flag emoji for a language
  const getLanguageFlag = (language) => {
    const languageMap = {
      'english': '🇬🇧',
      'spanish': '🇪🇸',
      'french': '🇫🇷',
      'german': '🇩🇪',
      'italian': '🇮🇹',
      'portuguese': '🇵🇹',
      'russian': '🇷🇺',
      'chinese': '🇨🇳',
      'japanese': '🇯🇵',
      'korean': '🇰🇷',
      'arabic': '🇸🇦',
      'hindi': '🇮🇳',
      'dutch': '🇳🇱',
      'swedish': '🇸🇪',
      'norwegian': '🇳🇴',
      'danish': '🇩🇰',
      'finnish': '🇫🇮',
      'greek': '🇬🇷',
      'turkish': '🇹🇷',
      'polish': '🇵🇱',
      'czech': '🇨🇿',
      'hungarian': '🇭🇺',
      'romanian': '🇷🇴',
      'bulgarian': '🇧🇬',
      'ukrainian': '🇺🇦'
    };
    
    const langLower = language.toLowerCase();
    return languageMap[langLower] || '🌐';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-xl font-semibold text-gray-800">Spoken Languages</h4>
      </div>
      
      {!spokenLanguageData || spokenLanguageData.length === 0 ? (
        <p className="text-gray-600">No spoken language data available.</p>
      ) : (
        <div className="space-y-4">
          {spokenLanguageData.map((language, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex flex-col md:flex-row">
                {/* Language Score Display - Left Side */}
                <div className="md:w-1/4 flex flex-col items-start mb-4 md:mb-0">
                  <div className="w-full max-w-[150px]">
                    <div className={`text-center p-4 rounded-lg ${getColorForScore(language.score)}`}>
                      <p className="text-5xl font-bold">{language.score}</p>
                      <div className="mt-2 flex items-center justify-center">
                        <span className="text-2xl mr-2">{getLanguageFlag(language.lang)}</span>
                        <p className="text-xl font-semibold">{language.lang}</p>
                      </div>
                    </div>
                    
                    {language.cefrScore && (
                      <div className="mt-2 text-center p-4 rounded-lg">
                        <div className={`inline-block px-3 py-1 rounded-full font-medium ${getColorForCEFR(language.cefrScore)}`}>
                          CEFR: {language.cefrScore}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Language Rationale - Right Side */}
                <div className="md:w-3/4 md:pl-2">
                  <h5 className="text-lg font-medium text-gray-800 mb-2">{language.lang} Proficiency</h5>
                  {language.rationale ? (
                    <p className="text-gray-600 mb-3">{language.rationale}</p>
                  ) : (
                    <p className="text-gray-500 italic mb-3">No rationale available.</p>
                  )}
                  
                  {language.evidence && language.evidence.length > 0 && (
                    <div>
                      <h6 className="font-medium text-gray-700 mb-1">Evidence:</h6>
                      <ul className="list-disc pl-5 space-y-1 text-gray-600">
                        {language.evidence.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {language.source && (
                    <div className="mt-3 text-xs text-gray-500">
                      Source: {Array.isArray(language.source) ? language.source.join(', ') : language.source}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpokenLanguageSummary;
